import useAddComment from "@babackapp/private-api-client/client/use-add-comment";
import { useUpdateReturnRequestInCache } from "@babackapp/private-api-client/client/use-invalidate-return-request";
import useTranslation from "@babackapp/translations/use-translation";
import { AutosizeTextarea } from "@babackapp/ui/components/autosize-textarea";
import { Button } from "@babackapp/ui/components/button";
import { Form, FormField } from "@babackapp/ui/components/form";
import { Loader, SendIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import z from "zod";

const formSchema = z.object({
	comment: z.string().min(1).max(255),
});

type ActivityInputProps = {
	returnRequestId: string;
	shopId: string;
};

export default function ActivityInput({
	returnRequestId,
	shopId,
}: ActivityInputProps) {
	const t = useTranslation();
	const updateReturnRequestInCache = useUpdateReturnRequestInCache();
	const form = useForm<z.infer<typeof formSchema>>({
		defaultValues: {
			comment: "",
		},
	});
	const { mutate, isPending } = useAddComment({
		onSuccess(returnRequest) {
			form.reset();
			updateReturnRequestInCache({
				returnRequest,
			});
		},
	});

	const onSubmit = form.handleSubmit((data) => {
		mutate({
			body: {
				comment: data.comment,
			},
			params: {
				path: {
					returnRequestId,
					shopId,
				},
			},
		});
	});

	const handleSumbitOnCtrlEnterKeyDown = (
		event: React.KeyboardEvent<HTMLTextAreaElement>,
	) => {
		if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
			event.preventDefault();
			onSubmit();
		}
	};

	return (
		<div className="relative">
			<Form {...form}>
				<form onSubmit={onSubmit}>
					<FormField
						render={({ field }) => {
							return (
								<AutosizeTextarea
									className="pb-12 bg-background resize-none pt-3 px-4 border border-border rounded-md"
									placeholder={t("returnDetail.comment.placeholder")}
									onChange={field.onChange}
									value={field.value}
									onKeyDown={handleSumbitOnCtrlEnterKeyDown}
								/>
							);
						}}
						name={"comment"}
					/>
					<div className="absolute bottom-3 right-4">
						<Button size="sm" disabled={isPending || !form.formState.isValid}>
							{isPending ? (
								<div className="absolute inset-0 rounded-full z-30 pointer-events-none flex items-center justify-center">
									<Loader className="w-4 h-4" />
								</div>
							) : (
								<>
									Send
									<SendIcon className={"h-4 w-4"} />
								</>
							)}
						</Button>
					</div>
				</form>
			</Form>
		</div>
	);
}
